import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Reveal, SectionLabel, BtnRed, BtnGhost, CtaBanner } from '../components/common';
import { images, projectCases, projectContexts } from '../mock/mock';
import { Link } from 'react-router-dom';

export default function EnMovimiento() {
  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <img src={images.crowd} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b0b0d]/85 via-[#0b0b0d]/65 to-[#0b0b0d]" />
        <div className="relative max-w-[1280px] mx-auto px-5 md:px-8 pt-28 w-full">
          <Reveal><SectionLabel light>Ideas que ya encontraron su ritmo</SectionLabel></Reveal>
          <Reveal delay={100}>
            <h1 className="font-display font-bold uppercase text-4xl md:text-6xl leading-[1.02] text-white max-w-3xl">
              Los proyectos hablan mejor
              <span className="block text-[var(--mt-red)]">cuando se ven en acción.</span>
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-7 max-w-xl text-white/70 leading-relaxed">
              Cada espacio plantea una necesidad distinta. Aquí es donde todo deja de ser una propuesta y empieza a suceder.
            </p>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <BtnRed to="/hablemos">Cuéntanos el tuyo</BtnRed>
              <BtnGhost to="/hazlo-posible">Cómo trabajamos</BtnGhost>
            </div>
          </Reveal>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 md:py-28 mt-checker">
        <div className="max-w-[1000px] mx-auto px-5 md:px-8">
          <Reveal><SectionLabel>Cada proyecto empieza con una pregunta</SectionLabel></Reveal>
          <Reveal delay={100}>
            <h2 className="font-display font-bold uppercase text-2xl md:text-4xl text-white leading-tight">
              ¿Qué necesita este espacio para cobrar vida?
            </h2>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-6 text-white/60 leading-relaxed max-w-2xl">
              No trabajamos con soluciones repetidas. Escuchamos el contexto, entendemos el objetivo y construimos la
              combinación adecuada de música, entretenimiento, producción y equipo. Por eso cada proyecto tiene una
              forma distinta. Y también un ritmo propio.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FEATURED PROJECT */}
      <section className="pb-24 md:pb-28">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <Reveal>
            <div className="relative overflow-hidden bg-[var(--mt-bg-alt)] border border-white/5 grid md:grid-cols-2">
              <div className="relative min-h-[340px]">
                <img src={images.openair} alt="Proyecto destacado" className="absolute inset-0 w-full h-full object-cover grayscale brightness-[0.6]" />
                <div className="absolute top-5 left-5 bg-[var(--mt-red)] px-4 py-1.5">
                  <span className="font-display uppercase tracking-wider text-xs text-white">Proyecto destacado</span>
                </div>
              </div>
              <div className="p-8 md:p-12">
                <h3 className="font-display font-bold uppercase text-2xl md:text-3xl text-white leading-tight">
                  Una programación cultural capaz de convertir una plaza en punto de encuentro.
                </h3>
                <div className="mt-6 space-y-4 text-sm">
                  <div><span className="text-[var(--mt-red)] font-display uppercase tracking-wider text-xs">El punto de partida</span><p className="text-white/60 mt-1">Un espacio con posibilidades, pero sin una propuesta estable que generara continuidad ni identidad.</p></div>
                  <div><span className="text-[var(--mt-red)] font-display uppercase tracking-wider text-xs">La idea</span><p className="text-white/60 mt-1">Diseñar una programación capaz de atraer públicos diferentes y mantener una línea reconocible a lo largo del tiempo.</p></div>
                  <div><span className="text-[var(--mt-red)] font-display uppercase tracking-wider text-xs">El resultado</span><p className="text-white/60 mt-1">Un espacio más vivo, una programación con sentido y nuevas razones para volver.</p></div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CONTEXTS */}
      <section className="bg-[var(--mt-bg-alt)] py-24 md:py-32">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <Reveal>
            <SectionLabel>No todos los espacios piden lo mismo</SectionLabel>
            <h2 className="font-display font-bold uppercase text-3xl md:text-5xl text-white leading-tight max-w-2xl">
              Diferentes lugares. Diferentes maneras de activar.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
            {projectContexts.map((c, i) => (
              <Reveal key={c.title} delay={(i % 3) * 100}>
                <div className="bg-[var(--mt-bg-card)] border border-white/5 p-7 h-full hover:border-[var(--mt-red)]/50 transition-colors">
                  <span className="mt-red-bar" />
                  <h3 className="font-display font-semibold uppercase text-lg text-white mt-4 tracking-wide">{c.title}</h3>
                  <p className="mt-3 text-white/55 text-sm leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECT CASES */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <Reveal>
            <SectionLabel>Selección de proyectos</SectionLabel>
            <h2 className="font-display font-bold uppercase text-3xl md:text-5xl text-white leading-tight max-w-2xl">
              Cuando todas las piezas encuentran su ritmo.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6 mt-14">
            {projectCases.map((p, i) => (
              <Reveal key={p.title} delay={(i % 2) * 100}>
                <div className="group bg-[var(--mt-bg-alt)] border border-white/5 overflow-hidden h-full">
                  <div className="relative h-56 overflow-hidden">
                    <img src={p.img} alt={p.title} className="w-full h-full object-cover grayscale brightness-[0.55] group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  </div>
                  <div className="p-7">
                    <h3 className="font-display font-bold uppercase text-xl text-white leading-tight">{p.title}</h3>
                    <div className="mt-4 space-y-2 text-sm">
                      <p className="text-white/60"><span className="text-[var(--mt-red)] font-medium">Necesidad: </span>{p.need}</p>
                      <p className="text-white/60"><span className="text-[var(--mt-red)] font-medium">Propuesta: </span>{p.proposal}</p>
                      <p className="text-white/60"><span className="text-[var(--mt-red)] font-medium">Resultado: </span>{p.result}</p>
                    </div>
                    <Link to="/hablemos" className="mt-5 inline-flex items-center gap-2 font-display uppercase tracking-wider text-sm text-white hover:text-[var(--mt-red)] transition-colors">
                      Descubrir el proyecto <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner
        label="El siguiente proyecto todavía no tiene nombre"
        title="Cuéntanos qué"
        redTitle="quieres activar."
        text="Puede empezar por un espacio, por una necesidad o simplemente por una idea. Nosotros empezaremos por entender qué puede llegar a suceder."
        cta="Hablemos de tu proyecto"
        image={images.stadium}
      />
    </div>
  );
}
